import { Injectable } from '@angular/core';
import { OrganizationApiClientService, OrganizationDto } from '@as/api';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { CurrentUserProxyService } from './current-user-proxy.service';
import { AuthStoreService } from './services/auth-store.service';
import { AuthService } from './services/auth.service';
import { IUserOrganizationDto } from './user-organization.dto';

@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private userOrganizations: BehaviorSubject<IUserOrganizationDto[]> = new BehaviorSubject([]);
  private currentUserOrganization: BehaviorSubject<IUserOrganizationDto> = new BehaviorSubject(null);
  private currentUserOrganizationDetails: BehaviorSubject<OrganizationDto> = new BehaviorSubject<OrganizationDto | null>(null);

  public userOrganizations$: Observable<IUserOrganizationDto[]>;
  public currentUserOrganization$: Observable<IUserOrganizationDto>;
  public currentUserOrganizationDetails$: Observable<OrganizationDto | null>;

  get currentOrganizationId(): string | undefined {
    return this.currentUserOrganization.value && this.currentUserOrganization.value.id;
  }

  get currentOrganizationName(): string | undefined {
    return this.currentUserOrganization.value && this.currentUserOrganization.value.name;
  }

  get currentOrganizationCountryCode(): string | null {
    const details = this.currentUserOrganizationDetails.value;
    return details && details.address && details.address.country ? details.address.country : null;
  }

  constructor(
    private currentUserProxyService: CurrentUserProxyService,
    private organizationService: OrganizationApiClientService,
    private authService: AuthService,
    private authStore: AuthStoreService
  ) {
    this.userOrganizations$ = this.userOrganizations.asObservable();
    this.currentUserOrganization$ = this.currentUserOrganization.asObservable();
    this.currentUserOrganizationDetails$ = this.currentUserOrganizationDetails.asObservable();
  }

  public clean() {
    this.userOrganizations.next(null);
    this.currentUserOrganizationDetails.next(null);
    this.currentUserOrganization.next(null);
  }

  public tryGetCurrentUserData$(): Observable<IUserOrganizationDto> {
    return this.getCurrentUserOrganization$();
  }

  private getCurrentUserOrganization$(): Observable<IUserOrganizationDto> {
    return this.currentUserOrganization$.pipe(
      switchMap(currentUserOrganization => {
        if (currentUserOrganization != null) {
          return of(currentUserOrganization);
        } else {
          return this.refresh$();
        }
      }),
      switchMap(organization => {
        return forkJoin({
          organization: of(organization),
          organizationDetails: this.getCurrentOrganizationDetailsAndUpdate(organization.id)
        });
      }),
      map(data => {
        return data.organization;
      }),
      take(1)
    );
  }

  public selectCurrentUserOrganization(selectedOrganization: IUserOrganizationDto): Observable<void> {
    return this.authService.refreshTokenOnOrganizationChange(selectedOrganization.id).pipe(
      tap(() => {
        this.currentUserOrganizationDetails.next(null);
        this.currentUserOrganization.next(selectedOrganization);
      }),
      switchMap(() => this.getCurrentOrganizationDetailsAndUpdate(selectedOrganization.id)),
      map(() => void 0)
    );
  }

  public getCurrentOrganizationDetailsAndUpdate(organizationId: string): Observable<OrganizationDto> {
    return this.organizationService.get(organizationId).pipe(
      tap(organizationDetails => {
        if (this.currentOrganizationId === organizationId) {
          this.currentUserOrganizationDetails.next(organizationDetails);
        }
      })
    );
  }

  private refresh$(): Observable<IUserOrganizationDto> {
    this.currentUserOrganizationDetails.next(null);

    return this.currentUserProxyService.getUserOrganizations().pipe(
      map(response => {
        if (response.length > 0) {
          this.userOrganizations.next(response);
          const defaultOrganization = this.getDefaultOrganization(response);
          this.currentUserOrganization.next(defaultOrganization);

          return defaultOrganization;
        }

        throw new Error(`Organization for user is not defined`);
      }),
      take(1)
    );
  }

  private getDefaultOrganization(organizations: IUserOrganizationDto[]): IUserOrganizationDto | null {
    if (organizations.length > 0) {
      const organizationId = this.authStore.getUserOrganizationId;
      return organizations.find(x => x.id === organizationId) || organizations[0];
    }

    return null;
  }
}
